import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";
import ExtensionIcon from "@mui/icons-material/Extension";
import RefreshIcon from "@mui/icons-material/Refresh";

import TaggingService from "../../services/TaggingService";

const EsimExtension = () => {
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extensionLoading, setExtensionLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const loadExpiringDevices = async () => {
    setLoading(true);

    try {
      const response = await TaggingService.getExpiringSoonDevices({});

      console.log("Expiring Soon Response:", response);

      const responseData = response?.data;

      let deviceList = [];

      if (Array.isArray(responseData)) {
        deviceList = responseData;
      } else if (Array.isArray(responseData?.results)) {
        deviceList = responseData.results;
      } else if (Array.isArray(responseData?.data)) {
        deviceList = responseData.data;
      }

      setRows(deviceList);
      setSelectedRows([]);
    } catch (error) {
      console.error("Failed to load expiring devices:", error);

      showMessage(
        error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to load expiring devices.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpiringDevices();
  }, []);

  const selectableRows = useMemo(() => {
    return rows.filter(
      (row) =>
        !row.extension_requested &&
        !row.extension_approved
    );
  }, [rows]);

  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) =>
      selectedRows.includes(row.device_stock_id)
    );

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(
        selectableRows.map((row) => row.device_stock_id)
      );
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (deviceStockId) => {
    setSelectedRows((previous) => {
      if (previous.includes(deviceStockId)) {
        return previous.filter((id) => id !== deviceStockId);
      }

      return [...previous, deviceStockId];
    });
  };

  const handleRequestExtension = async () => {
    if (!selectedRows.length) {
      showMessage(
        "Please select at least one device.",
        "warning"
      );
      return;
    }

    setExtensionLoading(true);

    try {
      const response =
        await TaggingService.requestEsimExtension(
          selectedRows
        );

      console.log("Extension Response:", response);

      const results = response?.data?.results || [];

      const successCount = results.filter(
        (item) => item.status === "success"
      ).length;

      const skippedCount = results.filter(
        (item) => item.status === "skipped"
      ).length;

      if (skippedCount > 0) {
        showMessage(
          `${successCount} extension request(s) submitted, ${skippedCount} skipped.`,
          successCount > 0 ? "warning" : "error"
        );
      } else {
        showMessage(
          `${successCount || selectedRows.length} extension request(s) submitted successfully.`,
          "success"
        );
      }

      setSelectedRows([]);

      // Refresh so the latest request state is displayed
      await loadExpiringDevices();
    } catch (error) {
      console.error(
        "Failed to request eSIM extension:",
        error
      );

      showMessage(
        error?.response?.data?.error ||
          error?.response?.data?.detail ||
          "Failed to request eSIM extension.",
        "error"
      );
    } finally {
      setExtensionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getStatus = (row) => {
    if (row.extension_approved) {
      return (
        <Chip
          label="Extension Approved"
          color="success"
          size="small"
        />
      );
    }

    if (row.extension_requested) {
      return (
        <Chip
          label="Extension Requested"
          color="warning"
          size="small"
        />
      );
    }

    return (
      <Chip
        label="Eligible"
        color="info"
        size="small"
      />
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Grid item>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ExtensionIcon color="primary" />

            <Box>
              <Typography
                variant="h4"
                fontWeight={600}
              >
                eSIM Extension
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Devices whose eSIM expires within the next
                90 days
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadExpiringDevices}
            disabled={loading || extensionLoading}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Summary */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Expiring Devices
              </Typography>

              <Typography
                variant="h4"
                fontWeight={600}
              >
                {rows.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Selected
              </Typography>

              <Typography
                variant="h4"
                fontWeight={600}
              >
                {selectedRows.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Eligible for Extension
              </Typography>

              <Typography
                variant="h4"
                fontWeight={600}
              >
                {selectableRows.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={
            extensionLoading ? (
              <CircularProgress
                size={18}
                color="inherit"
              />
            ) : (
              <ExtensionIcon />
            )
          }
          disabled={
            selectedRows.length === 0 ||
            extensionLoading
          }
          onClick={handleRequestExtension}
        >
          {extensionLoading
            ? "Requesting..."
            : `Request 1-Year Extension${
                selectedRows.length
                  ? ` (${selectedRows.length})`
                  : ""
              }`}
        </Button>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          overflow: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelectableSelected}
                  indeterminate={
                    selectedRows.length > 0 &&
                    !allSelectableSelected
                  }
                  onChange={handleSelectAll}
                  disabled={
                    loading ||
                    selectableRows.length === 0
                  }
                />
              </TableCell>

              <TableCell>
                <strong>IMEI</strong>
              </TableCell>

              <TableCell>
                <strong>Vehicle Reg. No.</strong>
              </TableCell>

              <TableCell>
                <strong>Chassis No.</strong>
              </TableCell>

              <TableCell>
                <strong>Owner Name</strong>
              </TableCell>

              <TableCell>
                <strong>Owner Phone</strong>
              </TableCell>

              <TableCell>
                <strong>Tagged At</strong>
              </TableCell>

              <TableCell>
                <strong>eSIM Expiry</strong>
              </TableCell>

              <TableCell>
                <strong>Extension Requested At</strong>
              </TableCell>

              <TableCell>
                <strong>Extension Approved At</strong>
              </TableCell>

              <TableCell>
                <strong>Status</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={11}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    No devices expiring within the next
                    90 days.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const deviceId =
                  row.device_stock_id;

                const isPending =
                  row.extension_requested &&
                  !row.extension_approved;

                const isApproved =
                  row.extension_approved;

                const isDisabled =
                  isPending || isApproved;

                return (
                  <TableRow
                    key={deviceId}
                    hover
                    selected={selectedRows.includes(
                      deviceId
                    )}
                  >
                    <TableCell padding="checkbox">
                      <Tooltip
                        title={
                          isApproved
                            ? "Extension already approved"
                            : isPending
                            ? "Extension request already pending"
                            : ""
                        }
                      >
                        <span>
                          <Checkbox
                            checked={selectedRows.includes(
                              deviceId
                            )}
                            disabled={isDisabled}
                            onChange={() =>
                              handleSelectRow(
                                deviceId
                              )
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      {row.imei || "-"}
                    </TableCell>

                    <TableCell>
                      {row.vehicle_reg_no || "-"}
                    </TableCell>

                    <TableCell>
                      {row.chassis_no || "-"}
                    </TableCell>

                    <TableCell>
                      {row.owner_name || "-"}
                    </TableCell>

                    <TableCell>
                      {row.owner_phone || "-"}
                    </TableCell>

                    <TableCell>
                      {formatDateTime(
                        row.tagged_at
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography
                        fontWeight={600}
                      >
                        {formatDate(
                          row.esim_expiry_date
                        )}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {formatDateTime(
                        row.extension_requested_at
                      )}
                    </TableCell>

                    <TableCell>
                      {formatDateTime(
                        row.extension_approved_at
                      )}
                    </TableCell>

                    <TableCell>
                      {getStatus(row)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() =>
          setSnackbar((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EsimExtension;